import { AssetClass } from "interfaces/enums";

const timeZone = "Europe/Berlin";
const locale = "de-DE";
const defaultFractionDigits = 2;

let localesSupported: boolean | undefined;

export function toLocaleDateStringSupportsLocales() {
    if (localesSupported !== undefined) {
        return localesSupported;
    }

    try {
        new Date().toLocaleDateString(locale, { timeZone });
        return (localesSupported = true);
    } catch (e) {
        return (localesSupported = !(e.name === "RangeError"));
    }
}

export function applyTimeShiftForIE11(inDate: number | string | Date): number | string | Date {
    if (toLocaleDateStringSupportsLocales()) {
        return inDate;
    }

    const testDate = new Date();
    const shiftedDate = new Date(inDate);
    shiftedDate.setTime(shiftedDate.getTime() + Math.abs(testDate.getTimezoneOffset()) * 60 * 1000);
    return shiftedDate;
}

const percentFormatter = Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: defaultFractionDigits,
    maximumFractionDigits: defaultFractionDigits
});

/**
 * Will format a given number value with thousands dots and with 2 fraction digits.
 * Example: 123456.20 => "123.456,20"
 */
export const formatDecimal = (inValue: number, fractionDigits: number = defaultFractionDigits): string => {
    const formatter = Intl.NumberFormat(locale, {
        style: "decimal",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    });

    return formatter.format(inValue);
};

/**
 * Will format a given number value with thousands dots and with 0 or 2 fraction digits.
 * Example: 123456.20 => "123.456"
 * Example: 6.20 => "6,20"
 */
export const dynamicFormatDecimal = (inValue: number): string => {
    if (inValue < 10) {
        return formatDecimal(inValue);
    }
    return formatDecimal(inValue, 0);
};

/**
 * Will format a given number value as percentage value.
 * Example: 0.025 => "2,50%"
 */
export const formatPercent = (inValue: number | null): string =>
    inValue === null ? "-" : percentFormatter.format(inValue).replace(/\s/g, "");

/**
 * Will act like `formatDecimal` but appends the currency string to the end.
 */
export const formatMoney1 = (
    inValue?: number | null,
    currency?: string | null,
    fractionDigits: number = defaultFractionDigits
): string => (inValue && currency ? `${formatDecimal(inValue, fractionDigits)} ${currency}` : "-");

export const formatMoney = (
    inValue?: number | null,
    currency?: string | null,
    fractionDigits: number = defaultFractionDigits
): string => {
    if (inValue && currency) {
        return `${formatDecimal(inValue, fractionDigits)} ${currency}`;
    } else if (inValue && !currency) {
        return `${formatDecimal(inValue, fractionDigits)}`;
    } else {
        return "-";
    }
};

/**
 * will format a passed marketValue depending on the passed assetClass as money or index value
 */
export const formatMarketValue = (
    inValue: number | null,
    currency: string | null | undefined,
    fractionDigits: number = defaultFractionDigits
): string => {
    if (inValue && !currency) {
        return `${formatDecimal(inValue, fractionDigits)}`;
    } else if (inValue && currency) {
        return `${formatDecimal(inValue, fractionDigits)} ${currency}`;
    } else {
        return "-";
    }
};

/**
 * Will format a given Date value to german date format.
 * Example: "26.01.20"
 */
export const formatDate = (inValue: number | string | Date): string =>
    new Date(applyTimeShiftForIE11(inValue)).toLocaleDateString(locale, {
        timeZone: toLocaleDateStringSupportsLocales() ? timeZone : "UTC",
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    });

/**
 * Will format a given Date value to german date format.
 * Example: "26.Januar.2021"
 */
export const formatDateLong = (inValue: number | string | Date): string =>
    new Date(applyTimeShiftForIE11(inValue)).toLocaleDateString(locale, {
        timeZone: toLocaleDateStringSupportsLocales() ? timeZone : "UTC",
        year: "numeric",
        month: "long",
        day: "2-digit"
    });

/**
 * Will format a given date value to german time format with seconds.
 * Example: "13:00:00"
 */
export const formatTime = (inValue: number | string | Date): string =>
    new Date(applyTimeShiftForIE11(inValue)).toLocaleTimeString(locale, {
        timeZone: toLocaleDateStringSupportsLocales() ? timeZone : "UTC",
        hour: "2-digit",
        minute: "2-digit"
    });

/**
 * Will either return a date string ("01.12.2019"), or - if the date is today,
 * will return a time: "12:00:00".
 * @param inValue
 */
export const formatDateOrTime = (inValue: number | string | Date, yesterdayAsText?: boolean): string => {
    const today = formatDate(new Date());
    const targetDate = formatDate(inValue);

    if (yesterdayAsText) {
        const d = new Date();
        const yesterday = formatDate(d.setDate(d.getDate() - 1));

        if (targetDate === yesterday) {
            return "Gestern";
        }
    }

    if (targetDate === today) {
        return formatTime(inValue) + " Uhr";
    }

    return targetDate;
};

/**
 * Will append million suffix.
 * In value is expected to be already in millions
 * Example: 50,651 => "50.651 Mio."
 */
export const appendMillionsSuffix = (inValue: number): string => {
    return `${formatDecimal(inValue, 0)} Mio.`;
};

/**
 * Will act like `appendMillionsSuffix` but appends the currency string to the end.
 * Example: 50,651 => "50.651 Mio. EUR"
 */
export const millionsWithCurrency = (inValue: number, currency: string): string => {
    return `${appendMillionsSuffix(inValue)} ${currency}`;
};
